import {
    Accordion,
    AccordionHeader,
    AccordionBody,
} from "@material-tailwind/react";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import InfoOnline from "../../components/InfoOnline";
import NotPermissionComponent from "../NotPermissionComponent";
import {useFindComplaints} from "../../../services/apis/apiLegalInformation";
import NoRecords from "../../components/NoRecords";
import {ItemInfo} from "../../components/ViewData";
import * as IconsMui from "@mui/icons-material";
import {formatDateHoursString} from "../../../utils/daysOptions";
import CustomTable from "../../../components/general/CustomTable";
import {Person} from "../../../services/interface/query/person";
import {ComplaintV2Interface} from "../../../services/interface/query/legal-information/complaintv2-interface";
interface Props {
    sri?: boolean;
    person?: Person;
}
export default function Complaints({sri, person}: Props) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: findLegalInformation,
        data: successLegalInformation,
        error: errorLegalInformation,
        isPending: loadingLegalInformation,
    } = useFindComplaints();

    const [response, setResponse] = useState<ComplaintV2Interface[] | undefined>();
    const [openAccordion, setOpenAccordion] = useState<number | null>(null);

    const handleOpen = (index: number) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const find = () => {
        if (person && person.identification && !sri) {
            setResponse(undefined);
            findLegalInformation({ identifier: person.identification, msj: t("criminal-record") });
        } else if (sri && person?.taxIdentificationNumber) {
            setResponse(undefined);
            findLegalInformation({ identifier: person.taxIdentificationNumber, msj: t("criminal-record") });
        }
    };

    useEffect(() => {
        find();
    }, [person]);

    useEffect(() => {
        if (successLegalInformation) {
            setResponse(successLegalInformation);
            if (successLegalInformation.length > 0) {
                setOpenAccordion(0);
            }
        }
    }, [successLegalInformation]);

    return (
        <div className="flex flex-col gap-3">
            <h2 className="text-center text-lg font-semibold text-gray-900 mb-6 w-full">
                {t("complaint-description")}
            </h2>

            <div className="flex flex-row gap-2">
                <h3>{t("complaints")}</h3>
                <InfoOnline onClick={find} />
            </div>

            <NotPermissionComponent
                title={"/legal_cases/complaints"}
                loading={loadingLegalInformation}
                error={errorLegalInformation}
                component={
                    <>
                        {!loadingLegalInformation && response && response.length > 0 ? (
                            response.map((item, index) => (
                                <Accordion
                                    key={`accordion-complaint-${index}`}
                                    open={openAccordion === index}
                                    icon={<IconsMui.ExpandMore />}
                                    placeholder=""
                                >
                                    <AccordionHeader onClick={() => handleOpen(index)} placeholder="">
                                        <div className="text-base">
                                            {`${t("complaint")} #${index + 1}`}
                                        </div>
                                    </AccordionHeader>
                                    <AccordionBody>
                                        <div className="custom-card p-3 flex flex-col gap-3">
                                            <div className="!border border-b-secondary border-transparent">
                                                {`${t("complaint")} #${index + 1}`}
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                <ItemInfo
                                                    label={t("nro-notice")}
                                                    text={item?.processNumber}
                                                    icon={<IconsMui.ReceiptLongTwoTone />}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t("nro-office")}
                                                    text={item?.relatedProcessNumber}
                                                    icon={<IconsMui.FormatListNumbered />}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t("crime")}
                                                    text={item?.legalScope}
                                                    icon={<IconsMui.AccountBalanceTwoTone />}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t("date-hours")}
                                                    text={formatDateHoursString(item?.dateRegister)}
                                                    icon={<IconsMui.EventTwoTone />}
                                                    classNameValue="text-sm"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 gap-3">
                                                <ItemInfo
                                                    label={t("unit")}
                                                    text={item?.location}
                                                    icon={<IconsMui.FmdGoodTwoTone />}
                                                    classNameValue="text-sm"
                                                />
                                            </div>

                                            {item.actions && item.actions.length > 0 && (
                                                <>
                                                    <div className="capitalize text-empColorPrimary font-semibold -mb-2 text-[14px]">
                                                        {`${t("detail")} ${t("complaints")}`}
                                                    </div>
                                                    <CustomTable
                                                        key={`table-complaint-${index}`}
                                                        header={[
                                                            t("identification"),
                                                            t("names"),
                                                            t("status"),
                                                        ]}
                                                        listElement={item.actions.flatMap((complaint, i) =>
                                                            (complaint.defendant || []).map((def, j) => ({
                                                                row: [
                                                                    def.identification || "-",
                                                                    def.names || "-",
                                                                    def.status || "-",
                                                                ]
                                                            }))
                                                        )}
                                                    />
                                                </>
                                            )}

                                            {item.vehicleInformation && item.vehicleInformation.length > 0 && (
                                                <div className="mt-4">
                                                    <div className="capitalize text-empColorPrimary font-semibold text-[14px] mb-2">
                                                        {t("vehicles-involved")}
                                                    </div>
                                                    <CustomTable
                                                        header={[t("plate"), t("brand"), t("model-vehicule")]}
                                                        listElement={item.vehicleInformation.map((v) => ({
                                                            row: [
                                                                v.carIdentifierPlate || "-",
                                                                v.carBrand || "-",
                                                                v.carModel || "-"
                                                            ]
                                                        }))}
                                                    />
                                                </div>
                                            )}

                                        </div>
                                    </AccordionBody>
                                </Accordion>
                            ))
                        ) : (
                            <NoRecords />
                        )}
                    </>
                }
            />
        </div>
    );
}
